/**
 * FT-122: Compliance Register Hook
 * FT-176: Added archive pattern for delete (sync compatibility)
 *
 * Manages compliance items (certificates, policy review dates, SMS document reviews)
 * with expiry tracking and traffic light status.
 *
 * Storage: compliance_register (vessel-scoped via useStorageState)
 * Sync: sr.compliance_register.v1
 */

import { useCallback, useMemo } from 'react';
import { useStorageState } from '../useStorageState';
import { useVessel } from '../../contexts/VesselContext';
import { usePermissions } from '../usePermissions';
import { devLog } from '../../utils/devLog';

// Traffic light status calculation
const calculateStatus = (expiryDate) => {
  if (!expiryDate) return 'not_set';

  const now = new Date();
  const expiry = new Date(expiryDate);
  const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

  if (daysUntilExpiry < 0) return 'expired';
  if (daysUntilExpiry <= 30) return 'expiring';
  return 'valid';
};

// Category display labels (FT-122c: renamed to match maritime terminology)
export const COMPLIANCE_CATEGORIES = {
  certificate: 'Certificate',
  sms_document: 'SMS Document',
  risk_assessment: 'Risk Assessment'
};

export const useComplianceRegister = () => {
  const { currentVesselId } = useVessel();
  const { currentUser } = usePermissions();

  // Base key only - vessel scoping handled by useStorageState
  const { value: rawItems = [], setValue: setItems } = useStorageState('compliance_register', []);

  // FT-176: Filter out archived items from read path
  // Items with status: 'archived' are soft-deleted (for sync compatibility)
  const items = useMemo(() => {
    return rawItems.filter(item => item.status !== 'archived');
  }, [rawItems]);

  /**
   * Add a new compliance item
   * FT-176: Use rawItems to preserve archived items
   */
  const addItem = useCallback(async (itemData) => {
    const now = new Date().toISOString();
    const newItem = {
      ...itemData,
      id: `comp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      status: calculateStatus(itemData.expiryDate),
      vesselId: currentVesselId,
      createdAt: now,
      updatedAt: now,
      createdBy: {
        userId: currentUser?.id,
        name: currentUser?.name,
        role: currentUser?.role
      }
    };

    const updated = [...rawItems, newItem];
    await setItems(updated);
    return newItem;
  }, [rawItems, setItems, currentVesselId, currentUser]);

  /**
   * Update an existing compliance item
   * FT-176: Use rawItems to preserve archived items
   */
  const updateItem = useCallback(async (itemId, updates) => {
    const now = new Date().toISOString();
    const updated = rawItems.map(item => {
      if (item.id === itemId) {
        const newData = {
          ...item,
          ...updates,
          status: calculateStatus(updates.expiryDate ?? item.expiryDate),
          updatedAt: now,
          updatedBy: {
            userId: currentUser?.id,
            name: currentUser?.name,
            role: currentUser?.role
          }
        };
        return newData;
      }
      return item;
    });

    await setItems(updated);
  }, [rawItems, setItems, currentUser]);

  /**
   * Delete a compliance item
   * FT-176: Uses archive pattern (set status: 'archived') instead of filtering
   * This allows sync to work properly with additive merge
   */
  const deleteItem = useCallback(async (itemId) => {
    const now = new Date().toISOString();
    const updated = rawItems.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          status: 'archived',
          archivedAt: now,
          archivedBy: {
            userId: currentUser?.id,
            name: currentUser?.name,
            role: currentUser?.role
          },
          updatedAt: now
        };
      }
      return item;
    });
    await setItems(updated);
    devLog(`[useComplianceRegister] FT-176: Archived item ${itemId}`);
  }, [rawItems, setItems, currentUser]);

  /**
   * Get items by category
   */
  const getByCategory = useCallback((category) => {
    if (!category || category === 'all') return items;
    return items.filter(item => item.category === category);
  }, [items]);

  /**
   * Summary statistics
   */
  const summary = useMemo(() => {
    const total = items.length;
    const valid = items.filter(i => i.status === 'valid').length;
    const expiring = items.filter(i => i.status === 'expiring').length;
    const expired = items.filter(i => i.status === 'expired').length;
    const notSet = items.filter(i => i.status === 'not_set').length;

    return { total, valid, expiring, expired, notSet };
  }, [items]);

  /**
   * Recalculate all statuses (call on mount/refresh to update stale data)
   * FT-176: Use rawItems to preserve archived items, but only update non-archived items
   */
  const refreshStatuses = useCallback(async () => {
    const updated = rawItems.map(item => {
      // Don't recalculate status for archived items
      if (item.status === 'archived') return item;
      return {
        ...item,
        status: calculateStatus(item.expiryDate)
      };
    });

    // Only update if something changed (for non-archived items)
    const hasChanges = rawItems.some((item, i) =>
      item.status !== 'archived' && item.status !== updated[i].status
    );
    if (hasChanges) {
      await setItems(updated);
    }
  }, [rawItems, setItems]);

  return {
    items,
    addItem,
    updateItem,
    deleteItem,
    getByCategory,
    summary,
    refreshStatuses,
    COMPLIANCE_CATEGORIES
  };
};

export default useComplianceRegister;
